import sanitizeHtml from "sanitize-html";

import {
    typesOfExperiences,
    skipJobTitles,
    score,
    addName,
    addPhoneNumber,
    addEmail,
    addUrl,
    addSkills,
    isAnIntern,
    notAJobTitle,
    addDatesToExperiences,
    addDatesToEducation,
    addSectionOrder,
    addChronologicalOrder,
    addMoreWordsBulletPoint,
    addMoreMetricsBulletPoint,
    addActionVerbsBulletPoint,
    addStrongActionVerbsBulletPoint,
    addRelevantInfoBulletPoints,
    addEducation,
    keepOneEducation,
    addDegree,
    addLocation,
    addUniversity,
    addWorkExperienceJobTitle,
    addWorkExperienceCompany,
    addWorkExperienceCity,
    addLeadershipOrganization,
    addLeadershipTitle,
    addLeadershipCity,
    addProjectOrganization,
    addProjectTitle,
    addProjectCity,
    increaseFontSize,
    decreaseFontSize,
    resumePagesExceeded,
    adjustMargins,
    addExperience,
    hasPhoneNumber,
    hasEmail,
    hasURL,
    hasNumbers,
    hasActionVerbs,
    isDate,
    isDateArrayInReverseChronologicalOrder,
    getScorePerFactor,
    getNumberOfWords,
    getStrongActionVerbs,
    generateRandomId,
    calculateNeededMetrics,
    findKeywords,
    removeHTMLTags,
    jobTitleIsRare,
} from "../utils/scorer-lib.js";
import { getKeywords } from "../api/scorer.js";
import { getBulletPointsFrom } from './builder-utils'

const notMetObject = {
    breakdownMessage: "notMet",
    score: 0,
};

const computeBulletPointStats = ({ bp, id }) => ({
    point: bp,
    length: getNumberOfWords(bp),
    hasStatistics: hasNumbers(bp),
    hasActionVerbs: hasActionVerbs(bp),
    warningId: id,
});

const removeHtml = (text) => {
    return sanitizeHtml(text, {
        allowedTags: [],
        allowedAttributes: []
    });
}

export const setBulletPointsScore = (
    bulletPointsRaw,
    bulletInfoRaw,
    breakdown
) => {
    const bulletPoints = [...(bulletPointsRaw || [])]?.map((bulletPoint) =>
        computeBulletPointStats(bulletPoint)
    );

    const bulletInfo = { ...bulletInfoRaw };

    const breakdownScoreBP = {};

    const fullPoints = [];
    const halfPoints = [];

    const successStatistics = [];
    const successActionVerbs = [];

    const scorePerFactor = getScorePerFactor(
        score.bulletPointContent.lengthFactor,
        bulletPoints.length
    );

    if (bulletPoints.length === 0) {
        const breakdownCopy = {
            ...breakdown,
            bulletPoints: {
                actionVerbs: notMetObject,
                length: notMetObject,
                statistics: notMetObject,
            },
        };

        return {
            bulletInfo,
            breakdownCopy,
        };
    }

    for (const point of bulletPoints) {
        if (!point) {
            continue;
        }

        // LENGTH - WARNINGS
        if (point.length >= 12) {
            fullPoints.push(point);
        } else if (point.length < 12 && point.length > 8) {
            halfPoints.push(point);
            bulletInfo[generateRandomId()] = addMoreWordsBulletPoint(point);
        } else if (point.length <= 8) {
            bulletInfo[generateRandomId()] = addMoreWordsBulletPoint(point);
        }

        // STATISTICS - WARNINGS
        if (point.hasStatistics) {
            successStatistics.push(point);
        }

        // ACTION VERBS - WARNINGS
        if (point.hasActionVerbs) {
            const { strongActionVerbsObject: strongActionVerbs } =
                getStrongActionVerbs(point);

            const refreshActionVerbs = () => getStrongActionVerbs(point);

            if (Object.keys(strongActionVerbs).length !== 0) {
                Object.entries(strongActionVerbs).forEach(([key, value]) => {
                    bulletInfo[generateRandomId()] =
                        addStrongActionVerbsBulletPoint(
                            point,
                            key,
                            value,
                            refreshActionVerbs
                        );
                });
            } else {
                successActionVerbs.push(point);
            }
        } else {
            bulletInfo[generateRandomId()] = addActionVerbsBulletPoint(point);
        }
    }

    // LENGTH - SCORE
    if (fullPoints.length === bulletPoints.length) {
        breakdownScoreBP.length = {
            breakdownMessage: "fullyMet",
            score: score.bulletPointContent.lengthFactor,
        };
    } else {
        if (fullPoints.length > 0 || halfPoints.length > 0) {
            let lengthScore = 0;

            if (fullPoints.length > 0) {
                lengthScore += scorePerFactor * fullPoints.length;
            }

            if (halfPoints.length > 0) {
                lengthScore += (scorePerFactor / 2) * halfPoints.length;
            }

            breakdownScoreBP.length = {
                breakdownMessage: "partiallyMet",
                score: lengthScore,
            };
        } else {
            breakdownScoreBP.length = notMetObject;
        }
    }

    // STATISTICS - SCORE
    const percentOfSuccessStatistics = Math.floor(
        (successStatistics.length / bulletPoints.length) * 100
    );

    if (percentOfSuccessStatistics >= 80) {
        breakdownScoreBP.statistics = {
            breakdownMessage: "fullyMet",
            score: score.bulletPointContent.statisticsFactor,
        };
    } else if (
        percentOfSuccessStatistics <= 79 &&
        percentOfSuccessStatistics >= 50
    ) {
        const metricsNeeded = calculateNeededMetrics(
            bulletPoints.length,
            successStatistics.length
        );

        breakdownScoreBP.statistics = {
            breakdownMessage: "partiallyMet",
            score: score.bulletPointContent.statisticsFactor / 2,
        };
        bulletInfo["addMetrics"] = addMoreMetricsBulletPoint(
            bulletPoints.length,
            successStatistics.length,
            metricsNeeded
        );
    } else {
        const metricsNeeded = calculateNeededMetrics(
            bulletPoints.length,
            successStatistics.length
        );

        breakdownScoreBP.statistics = notMetObject;
        bulletInfo["addMetrics"] = addMoreMetricsBulletPoint(
            bulletPoints.length,
            successStatistics.length,
            metricsNeeded
        );
    }

    // ACTION VERBS - SCORE
    if (
        successActionVerbs.length === bulletPoints.length &&
        successActionVerbs.length > 0
    ) {
        breakdownScoreBP.actionVerbs = {
            breakdownMessage: "fullyMet",
            score: score.bulletPointContent.actionVerbsFactor,
        };
    } else if (successActionVerbs.length > 0) {
        const score = scorePerFactor * successActionVerbs.length;

        breakdownScoreBP.actionVerbs = {
            breakdownMessage: "partiallyMet",
            score,
        };
    } else {
        breakdownScoreBP.actionVerbs = notMetObject;
    }

    const breakdownCopy = {
        ...breakdown,
        bulletPoints: breakdownScoreBP,
    };

    return { bulletInfo, breakdownCopy };
};

export const setFontFileSize = (
    nrOfPagesScorer,
    formattingFontFileSize,
    breakdown,
    textSizeCopy
) => {
    const fontSizePx = textSizeCopy + 14;

    let pagesNrPassed = false;
    let fontSizePassed = false;
    let readAbility = {};

    const formatingFontFileSizeCopy = { ...formattingFontFileSize };

    // check if font size is passed or return breakdown info
    if (fontSizePx >= 11 && fontSizePx <= 14) {
        fontSizePassed = true;
    } else {
        if (fontSizePx < 11) {
            formatingFontFileSizeCopy.fontSize = increaseFontSize();
        }
        if (fontSizePx > 14) {
            formatingFontFileSizeCopy.fontSize = decreaseFontSize();
        }
    }

    // check if pages number is passed or return breakdown info
    if (nrOfPagesScorer <= 2) {
        pagesNrPassed = true;
    } else {
        formatingFontFileSizeCopy.fontSize = resumePagesExceeded();
    }

    // setting up breakdown score
    if (fontSizePassed && pagesNrPassed) {
        readAbility = {
            breakdownMessage: "fullyMet",
            score: score.formatting.readAbility,
        };
    } else if (fontSizePassed || pagesNrPassed) {
        readAbility = {
            breakdownMessage: "partiallyMet",
            score: score.formatting.readAbility / 2,
        };
    } else {
        readAbility = notMetObject;
    }

    let formatting = breakdown?.formatting || {};

    formatting = {
        ...formatting,
        readAbility,
    };

    const breakdownCopy = { ...breakdown, formatting };

    return { breakdownCopy, formatingFontFileSizeCopy };
};

export const setMarginsSize = (margins, breakdown) => {
    const [leftMargin, rightMargin] = margins;

    let marginsBreakdown = {};
    const formattingMargins = {};

    if (leftMargin === rightMargin) {
        marginsBreakdown = {
            breakdownMessage: "fullyMet",
            score: score.formatting.margins,
        };
    } else {
        marginsBreakdown = notMetObject;
        formattingMargins[generateRandomId()] = adjustMargins();
    }

    let formatting = breakdown?.formatting || {};

    formatting = {
        ...formatting,
        margins: marginsBreakdown,
    };

    return {
        breakdown: { ...breakdown, formatting },
        formattingMargins:
            Object.keys(formattingMargins)?.length > 0 ? formattingMargins : {},
    };
};

export const setRelevantInfoPersonalInfo = (
    resumeCopy,
    breakdown,
    informationBi
) => {

    const name = removeHtml(resumeCopy?.name);
    const contact = removeHtml(resumeCopy?.contact);

    let personalInfoBreakdown = {};
    let passedConditions = 0;

    let relevantInformationCopy = breakdown?.relevantInformation || {};

    const newInformationBi = informationBi;

    const scorePerOneFactor = score.relevantInformation.personalInformation / 3;

    if (name !== undefined && name !== null && name.length !== 0) {
        passedConditions += 1;
    } else {
        newInformationBi.addName = addName();
    }
    if (contact !== undefined && contact !== null && contact.length !== 0) {
        if (hasPhoneNumber(contact)) {
            passedConditions++;
        } else {
            newInformationBi.phoneNumber = addPhoneNumber();
        }
        if (hasEmail(contact)) {
            passedConditions++;
        } else {
            newInformationBi.addEmail = addEmail();
        }
        if (hasURL(contact)) {
            const hasUrlObject = {
                breakdownMessage: "fullyMet",
                score: score.relevantInformation.hasUrl,
            };

            relevantInformationCopy = {
                ...relevantInformationCopy,
                hasUrl: hasUrlObject,
            };
        } else {
            relevantInformationCopy = {
                ...relevantInformationCopy,
                hasUrl: notMetObject,
            };

            newInformationBi.addUrl = addUrl();
        }
    } else {
        newInformationBi.phoneNumber = addPhoneNumber();
        newInformationBi.addEmail = addEmail();
        newInformationBi.addUrl = addUrl();

        relevantInformationCopy = {
            ...relevantInformationCopy,
            hasUrl: notMetObject,
        };
    }

    // complete breakdown
    if (passedConditions === 3) {
        personalInfoBreakdown = {
            breakdownMessage: "fullyMet",
            score: scorePerOneFactor * passedConditions,
        };
    } else if (passedConditions > 0) {
        personalInfoBreakdown = {
            breakdownMessage: "partiallyMet",
            score: scorePerOneFactor * passedConditions,
        };
    } else {
        personalInfoBreakdown = notMetObject;
    }

    relevantInformationCopy = {
        ...relevantInformationCopy,
        personalInformation: personalInfoBreakdown,
    };

    breakdown = {
        ...breakdown,
        relevantInformation: relevantInformationCopy,
    };

    return { breakdown, informationBi: newInformationBi };
};

export const setRelevantInfoBp = (breakdown, resume) => {
    const resumeSections = resume?.sections || [];

    const passedExperiences = [];
    const halfPassedExperiences = [];
    const experiencesWithBp = [];

    const newInformationBp = {};
    let relevantInfoBreakdown = {};

    resumeSections.forEach((experience) => {
        if (typesOfExperiences.includes(experience.type)) {
            experiencesWithBp.push(...experience.items);
        }
    });

    const scorePerFactor = getScorePerFactor(
        score.relevantInformation.bulletPoints,
        experiencesWithBp.length
    );

    experiencesWithBp.forEach((experience) => {
        const bulletPoints = getBulletPointsFrom(experience?.bullet_points);


        if (bulletPoints?.length >= 3) {
            passedExperiences.push(bulletPoints);
        } else {
            if (bulletPoints?.length > 0) {
                halfPassedExperiences.push(bulletPoints);
            }

            const missingPoints = 3 - bulletPoints?.length;

            newInformationBp[generateRandomId()] = addRelevantInfoBulletPoints(
                experience.id,
                missingPoints
            );
        }
    });

    if (
        passedExperiences.length === experiencesWithBp.length &&
        passedExperiences.length > 0
    ) {
        relevantInfoBreakdown = {
            breakdownMessage: "fullyMet",
            score: score.relevantInformation.bulletPoints,
        };
    } else if (passedExperiences.length > 0 || halfPassedExperiences.length) {
        let infoBpScore = 0;

        if (passedExperiences.length > 0) {
            infoBpScore += scorePerFactor * passedExperiences.length;
        }
        if (halfPassedExperiences.length > 0) {
            infoBpScore += (scorePerFactor / 2) * halfPassedExperiences.length;
        }

        relevantInfoBreakdown = {
            breakdownMessage: "partiallyMet",
            score: infoBpScore,
        };
    } else {
        relevantInfoBreakdown = notMetObject;
    }

    let relevantInformation = breakdown?.relevantInformation || {};

    const newBreakdown = {
        ...breakdown,
        relevantInformation: {
            ...relevantInformation,
            bulletPoints: relevantInfoBreakdown,
        },
    };

    return {
        newBreakdown,
        newInformationBp,
    };
};

export const setExperienceOrder = (breakdown, resume) => {
    const experiencesItems = [];
    const passedExperiences = [];
    const resumeSections = resume?.sections || [];

    let experienceDatesItems = [];
    let formattingExperienceOrder = {};

    let formatting = breakdown?.formatting || {};

    const newFormattingSectionOrder = {};

    const experiencesWithDates =
        resumeSections.filter((experience) => {
            return (
                !(experience.type === "custom_experience") &&
                !(experience.type === "education") &&
                experience.items.length > 1
            );
        }) || [];

    experiencesWithDates.forEach((experience) => {
        experiencesItems.push(...experience.items);

        if (typesOfExperiences.includes(experience.type)) {
            experienceDatesItems = experience.items.filter((item) => {
                return (
                    !item.date_hidden &&
                    (isDate(item.end_at) || item.end_at === "Present")
                );
            });
        }

        // do not send warnings for chronological order in case there are no dates
        if (
            experienceDatesItems.length === 0 ||
            experienceDatesItems.length != experiencesItems.length
        ) {
            return;
        }
        if (experienceDatesItems.length === experience.items.length) {
            const experienceDates = experienceDatesItems.map(
                (item) => item.end_at
            );

            if (isDateArrayInReverseChronologicalOrder(experienceDates)) {
                passedExperiences.push(experience);
            } else {
                newFormattingSectionOrder[generateRandomId()] =
                    addChronologicalOrder(experience.id);
            }
        } else if (experience.items.length > 0) {
            newFormattingSectionOrder[generateRandomId()] =
                addChronologicalOrder(experience.id);
        }
    });

    const scorePerFactor = getScorePerFactor(
        score.formatting.experienceOrder,
        experiencesWithDates.length
    );

    if (passedExperiences.length === experiencesWithDates.length) {
        formattingExperienceOrder = {
            breakdownMessage: "fullyMet",
            score: score.formatting.experienceOrder,
        };
    } else if (passedExperiences.length > 0) {
        formattingExperienceOrder = {
            breakdownMessage: "partiallyMet",
            score: scorePerFactor * passedExperiences.length,
        };
    } else {
        formattingExperienceOrder = notMetObject;
    }

    const newBreakdown = {
        ...breakdown,
        formatting: {
            ...formatting,
            experienceOrder: formattingExperienceOrder,
        },
    };

    return { newBreakdown, newFormattingSectionOrder };
};

export const setSectionFormatting = (
    breakdown,
    resume,
    user,
    formattingSectionFormat
) => {
    const experiences = resume?.sections || [];
    const userType = user?.options?.career_status;

    const newFormattingSectionFormat = { ...formattingSectionFormat };

    let firstSectionType;
    let neededExperience;
    let sectionFormat = {};

    if (experiences.length > 0) {
        firstSectionType = experiences[0]?.type;
    } else {
        return null;
    }

    if (
        (firstSectionType === "education" && userType === "Student") ||
        (firstSectionType === "work_experience" && userType !== "Student")
    ) {
        sectionFormat = {
            breakdownMessage: "fullyMet",
            score: score.formatting.sectionFormat,
        };
    } else {
        experiences.forEach((experience) => {
            if (
                (experience.type === "education" && userType === "Student") ||
                (experience.type === "work_experience" && userType != "Student")
            ) {
                neededExperience = experience;
            }
        });
        newFormattingSectionFormat["addSectionOrder"] = addSectionOrder(
            neededExperience?.id,
            userType
        );

        sectionFormat = notMetObject;
    }

    let formatting = breakdown?.formatting || {};

    formatting = {
        ...formatting,
        sectionFormat,
    };

    const newBreakdown = { ...breakdown, formatting };

    return {
        newBreakdown,
        newFormattingSectionFormat,
    };
};

export const setRelevantInfoExperienceDate = (
    breakdown,
    resume,
    informationDates
) => {
    const passedDates = [];
    const experiencesItems = [];
    const allExperiences = resume?.sections || [];
    let experiencesDateBreakdown = {};

    const informationDatesCopy = { ...informationDates };

    const experiencesWithDates =
        allExperiences.filter((experience) => {
            return !(experience.type === "custom_experience");
        }) || [];

    experiencesWithDates.forEach((experience) => {
        experiencesItems.push(...experience.items);
        experience.items.forEach((item) => {
            if (experience.type === "education") {
                if (item.date_hidden || item.date !== "Date") {
                    passedDates.push(item);
                } else {
                    informationDatesCopy[generateRandomId()] =
                        addDatesToEducation(item.id);
                }
            } else if (experience.type === "project_experience") {
                if (
                    item.date_hidden ||
                    (item.start_at !== "Start Date" &&
                        item.end_at !== "Finish Date")
                ) {
                    passedDates.push(item);
                } else {
                    informationDatesCopy[generateRandomId()] =
                        addDatesToExperiences(item.id);
                }
            } else {
                if (
                    !item.date_hidden &&
                    item.start_at !== "Start Date" &&
                    item.end_at !== "Finish Date"
                ) {
                    passedDates.push(item);
                } else {
                    informationDatesCopy[generateRandomId()] =
                        addDatesToExperiences(item.id);
                }
            }
        });
    });

    const scorePerFactorExperiencesDates = getScorePerFactor(
        score.relevantInformation.datesForExperiences,
        experiencesItems.length
    );

    if (
        experiencesItems.length === passedDates.length &&
        experiencesItems.length > 0
    ) {
        experiencesDateBreakdown = {
            breakdownMessage: "fullyMet",
            score: score.relevantInformation.datesForExperiences,
        };
    } else if (passedDates.length > 0) {
        experiencesDateBreakdown = {
            breakdownMessage: "partiallyMet",
            score: scorePerFactorExperiencesDates * passedDates.length,
        };
    } else {
        experiencesDateBreakdown = notMetObject;
    }

    let relevantInformation = breakdown?.relevantInformation || {};

    relevantInformation = {
        ...relevantInformation,
        datesForExperiences: experiencesDateBreakdown,
    };

    const breakdownCopy = {
        ...breakdown,
        relevantInformation,
    };

    return { breakdownCopy, informationDatesCopy };
};

export const setExperience = (resume) => {
    let experiencePopulation = {};
    const resumeSections = resume?.sections || [];

    const experiences = resumeSections.filter((experience) => {
        if (typesOfExperiences.includes(experience.type)) {
            return experience;
        }
    });

    if (experiences.length === 0) {
        experiencePopulation["addExperience"] = addExperience();
    }

    return experiencePopulation;
};

const proposeSkillsKeywords = (keywords, matchedKeywords, latestKeywords = []) => {
    let skills = [];
    let randomUsedValues = [];
    const keywordsCopy = keywords.filter(keyword => !matchedKeywords.includes(keyword))

    if (keywordsCopy.length <= 3) {
        return keywordsCopy;
    }

    while (skills.length < 3) {
        const random = Math.floor(Math.random() * keywordsCopy.length);
        if (
            randomUsedValues.includes(random) ||
            latestKeywords.includes(keywordsCopy[random])
        ) {
            continue;
        }
        skills.push(keywordsCopy[random]);
        randomUsedValues.push(random);
    }

    return skills;
};

export const getJobsAndBullets = (resume) => {
    let jobs = [];
    let bullets = [];
    let newInternSkillsJobWarning = {};

    resume?.sections.forEach((experience) => {
        if (experience.type === "work_experience") {
            experience.items.forEach((item) => {
                if (item.title == null) {
                    return;
                }

                const titleWithoutTags = removeHTMLTags(item.title);

                if (
                    skipJobTitles.includes(
                        titleWithoutTags.toLowerCase().trim()
                    )
                ) {
                    newInternSkillsJobWarning[generateRandomId()] = isAnIntern(
                        experience.id,
                        item.id,
                        titleWithoutTags
                    );
                    return;
                }

                if (item?.bullet_points != null && titleWithoutTags !== "") {
                    const bulletPoints = getBulletPointsFrom(item?.bullet_points)
                    const pointsInOneString = bulletPoints.map(bp => bp.bp).join(" ");

                    bullets.push(pointsInOneString);
                    
                    jobs.push({
                        sectionId: experience.id,
                        itemId: item.id,
                        jobTitle: titleWithoutTags.toLowerCase().trim(),
                        jobTitleOriginal: titleWithoutTags.trim(),
                    });
                }
            });
        }
    });

    return { jobs, bullets, newInternSkillsJobWarning };
};

export const calculateKeywords = async (jobs, bullets) => {
    const jobsCopy = [];
    let newSkillsNotPassedWarning = {};

    try {
        const jobTitles = jobs.map((job) => job.jobTitle);
        const result = await getKeywords(jobTitles);
        const keywordsObject = result?.data;

        jobs.forEach((job, index) => {
            if (keywordsObject[job.jobTitle] == undefined) {
                return;
            }

            const keywordsForOneJob =
                keywordsObject[job.jobTitle]?.keywords || [];
            const isValidJobTitle =
                keywordsObject[job.jobTitle]?.valid || false;
            const userRequestsCount =
                keywordsObject[job.jobTitle]?.user_count || 0;

            const itemId = job.itemId;
            const sectionId = job.sectionId;

            jobsCopy[index] = job;
            jobsCopy[index].keywords = keywordsForOneJob;

            if (!isValidJobTitle && userRequestsCount >= 5) {
                newSkillsNotPassedWarning[generateRandomId()] = notAJobTitle(
                    sectionId,
                    itemId,
                    job?.jobTitleOriginal
                );

                jobsCopy[index].isRare = false;
                jobsCopy[index].isValid = false;
            } else if (
                keywordsForOneJob.length === 0 &&
                userRequestsCount < 5
            ) {
                newSkillsNotPassedWarning[generateRandomId()] = jobTitleIsRare(
                    sectionId,
                    itemId,
                    job?.jobTitleOriginal
                );

                jobsCopy[index].isRare = true;
                jobsCopy[index].isValid = false;
            } else {
                const bullet = bullets[index];
                const matchedKeywords = findKeywords(bullet, keywordsForOneJob);

                jobsCopy[index].matchedKeywords = matchedKeywords;
                jobsCopy[index].isRare = false;
                jobsCopy[index].isValid = true;
            }
        });



        return { newJobs: jobsCopy, newSkillsNotPassedWarning };
    } catch (err) {
        return;
    }
};

export const setSkills = (jobs, breakdown, skillsAddKeywords) => {
    const nonrareJobs = jobs.filter((job) => !job.isRare && job.isValid);
    let skillsFactor = notMetObject;

    if (nonrareJobs.length === 0) {
        const newBreakdown = {
            ...breakdown,
            skills: skillsFactor,
        };

        return { newBreakdown, newSkillsAddKeywords: {} };
    }
    const scorePerFactor = getScorePerFactor(score.skills, nonrareJobs.length);
    let fullPassedJobs = 0;
    let halfPassedJobs = 0;
    let newSkillsAddKeywords = skillsAddKeywords;

    nonrareJobs.forEach((job) => {
        const refreshProposeKeywords = () =>
            proposeSkillsKeywords(job.keywords, job.matchedKeywords);

        if (job.matchedKeywords.length >= 5) {
            fullPassedJobs++;
        } else if (job.matchedKeywords.length < 5 && job.matchedKeywords.length > 0) {
            const proposedKeywords = proposeSkillsKeywords(job.keywords, job.matchedKeywords);

            newSkillsAddKeywords[generateRandomId()] = addSkills(
                job.itemId,
                job.matchedKeywords,
                proposedKeywords,
                job.keywords,
                refreshProposeKeywords
            );
            halfPassedJobs++;
        } else {
            const proposedKeywords = proposeSkillsKeywords(job.keywords, job.matchedKeywords);

            newSkillsAddKeywords[generateRandomId()] = addSkills(
                job.itemId,
                job.matchedKeywords,
                proposedKeywords,
                job.keywords,
                refreshProposeKeywords
            );
        }
    });

    if (fullPassedJobs === nonrareJobs.length) {
        skillsFactor = {
            breakdownMessage: "fullyMet",
            score: score.skills,
        };
    } else {
        if (fullPassedJobs > 0 || halfPassedJobs > 0) {
            let skillsScore = 0;

            if (fullPassedJobs > 0) {
                skillsScore += scorePerFactor * fullPassedJobs;
            }

            if (halfPassedJobs > 0) {
                skillsScore += (scorePerFactor / 2) * halfPassedJobs;
            }

            skillsFactor = {
                breakdownMessage: "partiallyMet",
                score: skillsScore,
            };
        }
    }

    const newBreakdown = {
        ...breakdown,
        skills: skillsFactor,
    };

    return { newBreakdown, newSkillsAddKeywords };
};

export const setEducationScore = (breakdown, resume) => {
    const newEducationInfo = {};
    let newBreakdownScoreEducation = {};

    let relevantInformation = breakdown?.relevantInformation || {};

    const educationSection =
        resume?.sections.filter(
            (experience) => experience.type === "education"
        ) || [];

    if (educationSection.length === 0) {
        newEducationInfo[generateRandomId()] = addEducation();

        relevantInformation = {
            ...relevantInformation,
            educationInfo: notMetObject,
        };

        newBreakdownScoreEducation = {
            ...breakdown,
            relevantInformation,
        };

        return { newBreakdownScoreEducation, newEducationInfo };
    }

    if (educationSection.length > 1) {
        newEducationInfo[generateRandomId()] = keepOneEducation();

        relevantInformation = {
            ...relevantInformation,
            educationInfo: notMetObject,
        };

        newBreakdownScoreEducation = {
            ...breakdown,
            relevantInformation,
        };

        return { newBreakdownScoreEducation, newEducationInfo };
    }

    const sectionId = educationSection[0].id;
    const educationItems = educationSection[0]?.items || [];

    let passedDegrees = 0;
    let passedUniversities = 0;
    let passedLocations = 0;

    educationItems.forEach((item) => {
        // degree
        if (item.title != null && removeHTMLTags(item.title) !== "") {
            passedDegrees++;
        } else {
            newEducationInfo[generateRandomId()] = addDegree(
                sectionId,
                item.id
            );
        }
        // university
        if (item.heading != null && removeHTMLTags(item.heading) !== "") {
            passedUniversities++;
        } else {
            if(item?.parent_uuid == null) {
                newEducationInfo[generateRandomId()] = addUniversity(
                    sectionId,
                    item.id
                );
            }else{
                passedUniversities++;
            }
        }
        // location
        if (
            item.location != null &&
            removeHTMLTags(item.location) !== ""
        ) {
            passedLocations++;
        } else {
            if(item?.parent_uuid == null) {
                newEducationInfo[generateRandomId()] = addLocation(
                    sectionId,
                    item.id
                );
            }else{
                passedLocations++;
            }
        }
    });

    let breakdownScoreEducation = {};

    // calculate breakdown
    if (
        passedDegrees === educationItems.length &&
        passedUniversities === educationItems.length &&
        passedLocations === educationItems.length
    ) {
        breakdownScoreEducation = {
            breakdownMessage: "fullyMet",
            score: score.relevantInformation.educationInfo,
        };
    } else if (
        passedDegrees > 0 ||
        passedUniversities > 0 ||
        passedLocations > 0
    ) {
        const scorePerFactor = getScorePerFactor(
            score.relevantInformation.educationInfo,
            3
        );
        let educationInfoScore = 0;

        if (passedDegrees > 0) {
            educationInfoScore += scorePerFactor;
        }
        if (passedUniversities > 0) {
            educationInfoScore += scorePerFactor;
        }
        if (passedLocations > 0) {
            educationInfoScore += scorePerFactor;
        }

        breakdownScoreEducation = {
            breakdownMessage: "partiallyMet",
            score: educationInfoScore,
        };
    } else {
        breakdownScoreEducation = notMetObject;
    }

    relevantInformation = {
        ...relevantInformation,
        educationInfo: breakdownScoreEducation,
    };

    newBreakdownScoreEducation = {
        ...breakdown,
        relevantInformation,
    };

    return { newBreakdownScoreEducation, newEducationInfo };
};

const getPassedFieldsWorkExperience = (workExperienceItem, sectionId) => {
    let passedItems = 0;
    let workExperienceInfo = {};

    // job title
    if (
        workExperienceItem.title != null &&
        removeHTMLTags(workExperienceItem.title) !== ""
    ) {
        passedItems++;
    } else {
        workExperienceInfo[generateRandomId()] = addWorkExperienceJobTitle(
            sectionId,
            workExperienceItem.id
        );
    }
    // company
    if (
        workExperienceItem.heading != null &&
        removeHTMLTags(workExperienceItem.heading) !== ""
    ) {
        passedItems++;
    } else {
        if(workExperienceItem?.parent_uuid == null) {
            workExperienceInfo[generateRandomId()] = addWorkExperienceCompany(
                sectionId,
                workExperienceItem.id
            );
        }else{
            passedItems++;
        }
    }
    // location
    if (
        workExperienceItem.location != null &&
        removeHTMLTags(workExperienceItem.location) !== ""
    ) {
        passedItems++;
    } else {
        if(workExperienceItem?.parent_uuid == null) {
            workExperienceInfo[generateRandomId()] = addWorkExperienceCity(
                sectionId,
                workExperienceItem.id
            );
        }else{
            passedItems++;
        }
    }

    return { passedItems, workExperienceInfo };
};

const getPassedFieldsLeadershipExprience = (
    leadershipExperienceItem,
    sectionId
) => {
    let passedItems = 0;
    let leadershipExperienceInfo = {};

    // title
    if (
        leadershipExperienceItem.title != undefined &&
        removeHTMLTags(leadershipExperienceItem.title) !== ""
    ) {
        passedItems++;
    } else {
        leadershipExperienceInfo[generateRandomId()] = addLeadershipTitle(
            sectionId,
            leadershipExperienceItem.id
        );
    }
    
    // organization
    if (
        leadershipExperienceItem.heading != null &&
        removeHTMLTags(leadershipExperienceItem.heading) !== ""
    ) {
        passedItems++;
    } else {
        if(leadershipExperienceItem?.parent_uuid == null){
            leadershipExperienceInfo[generateRandomId()] =
                addLeadershipOrganization(sectionId, leadershipExperienceItem.id);
        }else{
            passedItems++;
        }
    }
    // location
    if (
        leadershipExperienceItem.location != null &&
        removeHTMLTags(leadershipExperienceItem.location) !== ""
    ) {
        passedItems++;
    } else {
        if(leadershipExperienceItem?.parent_uuid == null) {
            leadershipExperienceInfo[generateRandomId()] = addLeadershipCity(
                sectionId,
                leadershipExperienceItem.id
            );
        }else{
            passedItems++;
        }
    }

    return { passedItems, leadershipExperienceInfo };
};

const getPassedFieldsProjectExprience = (projectExperienceItem, sectionId) => {
    let passedItems = 0;
    let projectExperienceInfo = {};

    // title
    if (
        projectExperienceItem.title != null &&
        removeHTMLTags(projectExperienceItem.title) !== ""
    ) {
        passedItems++;
    } else {
        projectExperienceInfo[generateRandomId()] = addProjectTitle(
            sectionId,
            projectExperienceItem.id
        );
    }
    // organization
    if (
        projectExperienceItem.heading != null &&
        removeHTMLTags(projectExperienceItem.heading) !== "" &&
        projectExperienceItem?.parent_uuid == null
    ) {
        passedItems++;
    } else {
        if(projectExperienceItem?.parent_uuid == null) {
            projectExperienceInfo[generateRandomId()] = addProjectOrganization(
                sectionId,
                projectExperienceItem.id
            );
        }else{
            passedItems++;
        }
    }
    // location
    if (
        projectExperienceItem.location != null &&
        removeHTMLTags(projectExperienceItem.location) !== "" &&
        projectExperienceItem?.parent_uuid == null
    ) {
        passedItems++;
    } else {
        if(projectExperienceItem?.parent_uuid == null) {
            projectExperienceInfo[generateRandomId()] = addProjectCity(
                sectionId,
                projectExperienceItem.id
            );
        }else{
            passedItems++;
        }
    }

    return { passedItems, projectExperienceInfo };
};

export const setExperienceScore = (breakdown, resume) => {
    let newExperiencesInfo = {};
    let newBreakdownScoreExperience = {};

    let passedExperiences = 0;
    let totalItemsAmount = 0;

    let relevantInformation = breakdown?.relevantInformation || {};

    const experienceSections =
        resume?.sections.filter((experience) =>
            typesOfExperiences.includes(experience.type)
        ) || [];

    experienceSections.forEach((experience) => {
        totalItemsAmount += experience.items.length * 3;

        if (experience.type === "work_experience") {
            experience.items.forEach((workExperienceItem) => {
                let { passedItems, workExperienceInfo } =
                    getPassedFieldsWorkExperience(
                        workExperienceItem,
                        experience.id
                    );

                newExperiencesInfo = {
                    ...newExperiencesInfo,
                    ...workExperienceInfo,
                };

                passedExperiences += passedItems;
            });
        } else if (experience.type === "leadership_experience") {
            experience.items.forEach((leadershipExperienceItem) => {
                let { passedItems, leadershipExperienceInfo } =
                    getPassedFieldsLeadershipExprience(
                        leadershipExperienceItem,
                        experience.id
                    );

                newExperiencesInfo = {
                    ...newExperiencesInfo,
                    ...leadershipExperienceInfo,
                };

                passedExperiences += passedItems;
            });
        } else if (experience.type === "project_experience") {
            experience.items.forEach((projectExperienceItem) => {
                let { passedItems, projectExperienceInfo } =
                    getPassedFieldsProjectExprience(
                        projectExperienceItem,
                        experience.id
                    );

                newExperiencesInfo = {
                    ...newExperiencesInfo,
                    ...projectExperienceInfo,
                };

                passedExperiences += passedItems;
            });
        }
    });

    let experienceInfo = {};

    const scorePerFactor = getScorePerFactor(
        score.relevantInformation.experienceInfo,
        totalItemsAmount
    );

    if (passedExperiences === totalItemsAmount) {
        experienceInfo = {
            breakdownMessage: "fullyMet",
            score: score.relevantInformation.experienceInfo,
        };
    } else if (passedExperiences > 0) {
        experienceInfo = {
            breakdownMessage: "partiallyMet",
            score: scorePerFactor * passedExperiences,
        };
    } else {
        experienceInfo = notMetObject;
    }

    relevantInformation = {
        ...relevantInformation,
        experienceInfo,
    };

    newBreakdownScoreExperience = {
        ...breakdown,
        relevantInformation,
    };

    return { newBreakdownScoreExperience, newExperiencesInfo };
};
